import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import {
  CardElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import toast from "react-hot-toast";
import { axiosInstance } from "../../api/axiosDefaults";
import appStyles from "../../App.module.css";
import TopBox from "../../components/TopBox";
import btnStyles from "../../styles/Button.module.css";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import AddPaymentMethodModal from "./AddPaymentMethodModal";
import PaymentMethodCard from "./PaymentMethodCard";
import styles from "../../styles/BillingPage.module.css";

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      fontWeight: 400,
      color: "#050F2F",
      "::placeholder": {
        color: "#9A98A8",
      },
    },
    invalid: {
      color: "#f54e4e",
      iconColor: "#f54e4e",
    },
  },
  hidePostalCode: true,
};

const emptyCard = {
  id: "",
  brand: "",
  last4: "",
  exp_month: "",
  exp_year: "",
  created_at: "",
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

const normalizePaymentMethods = (data) => {
  if (Array.isArray(data)) {
    return data.filter(Boolean);
  }

  if (Array.isArray(data?.payment_methods)) {
    return data.payment_methods.filter(Boolean);
  }

  return [];
};

const filterDefaultMethod = (methods, defaultMethod) => {
  const defaultId = defaultMethod?.id;

  return methods.filter((method) => {
    if (!defaultId || !method?.id) {
      return method !== defaultMethod;
    }
    return method.id !== defaultId;
  });
};

const getPaymentErrorMessage = (error, fallback) => {
  const responseError = error?.response?.data?.error;

  if (typeof responseError === "string") {
    return responseError;
  }

  return (
    error?.message ||
    responseError?.message ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    fallback
  );
};

export default function BillingPage() {
  const currentUser = useCurrentUser();
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [settingDefaultId, setSettingDefaultId] = useState("");
  const [customerMissing, setCustomerMissing] = useState(false);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(emptyCard);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [subscriptionWarnings, setSubscriptionWarnings] = useState([]);
  const [clientSecret, setClientSecret] = useState("");
  const [message, setMessage] = useState("");
  const [showCardModal, setShowCardModal] = useState(false);

  const otherCards = useMemo(
    () => filterDefaultMethod(paymentMethods, defaultPaymentMethod),
    [paymentMethods, defaultPaymentMethod]
  );

  const hasDefaultCard = Boolean(defaultPaymentMethod?.brand);

  const applyDefaultPaymentPayload = (data) => {
    const defaultMethod = data?.default_payment_method || emptyCard;
    setDefaultPaymentMethod(defaultMethod);
    setSubscriptionWarnings(data?.subscription_sync_warnings || []);
  };

  const fetchPaymentMethods = useCallback(async () => {
    const response = await axiosInstance.get("/billing/payment-methods/", {
      headers: authHeaders(),
      withCredentials: true,
    });

    if (response?.success === false) {
      throw response?.error;
    }

    setPaymentMethods(normalizePaymentMethods(response?.data?.data || []));
  }, []);

  const fetchPaymentMethod = useCallback(async () => {
    setLoading(true);
    setCustomerMissing(false);

    const response = await axiosInstance.get("/billing/payment-method/", {
      headers: authHeaders(),
      withCredentials: true,
    });

    if (response?.success === false) {
      if (response?.error?.response?.status === 404) {
        setCustomerMissing(true);
        setDefaultPaymentMethod(emptyCard);
        setPaymentMethods([]);
        setLoading(false);
        return;
      }

      throw response?.error;
    }

    applyDefaultPaymentPayload(response?.data?.data || {});
    await fetchPaymentMethods();
    setLoading(false);
  }, [fetchPaymentMethods]);

  useEffect(() => {
    fetchPaymentMethod().catch((err) => {
      console.log(err);
      toast.error("Unable to load payment methods.");
      setLoading(false);
    });
  }, [fetchPaymentMethod]);

  const startCardFlow = async () => {
    try {
      setLoadingIntent(true);
      setMessage("");

      const response = await axiosInstance.post(
        "/billing/setup-intent/",
        {},
        {
          headers: authHeaders(),
          withCredentials: true,
        }
      );

      if (response?.success === false) {
        throw response?.error;
      }

      const secret = response?.data?.data?.client_secret || "";
      if (!secret) {
        toast.error("Could not start card setup.");
        return;
      }

      setClientSecret(secret);
      setShowCardModal(true);
    } catch (err) {
      console.log(err);
      toast.error("Could not start card setup.");
    } finally {
      setLoadingIntent(false);
    }
  };

  const closeCardModal = () => {
    setShowCardModal(false);
    setClientSecret("");
    setMessage("");
  };

  const persistPaymentMethod = async (paymentMethodId) => {
    const response = await axiosInstance.post(
      "/billing/payment-method/",
      { payment_method_id: paymentMethodId },
      {
        headers: authHeaders(),
        withCredentials: true,
      }
    );
    if (response?.success === false) {
      throw response?.error;
    }

    applyDefaultPaymentPayload(response?.data?.data || {});
    await fetchPaymentMethods();
    return response?.data?.data;
  };

  const handleSetDefault = async (method) => {
    if (!method?.id) {
      toast.error("This payment method cannot be set as default.");
      return;
    }

    try {
      setSettingDefaultId(method.id);
      await persistPaymentMethod(method.id);
      toast.success("Default payment method updated.");
    } catch (err) {
      console.log(err);
      toast.error("Unable to update the default payment method.");
    } finally {
      setSettingDefaultId("");
    }
  };

  const handleSubmit = async (
    event,
    { addressLine1, country, state, city, postalCode } = {}
  ) => {
    event.preventDefault();
    setMessage("");

    if (!stripe || !elements) {
      return;
    }

    if (!country) {
      toast.error("Please select a country.");
      return;
    }

    if (!addressLine1 || !state || !city || !postalCode) {
      toast.error("Please complete the billing address.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error("Card fields are not ready yet.");
      return;
    }

    if (!clientSecret) {
      toast.error("Start the card setup flow first.");
      return;
    }

    try {
      setSaving(true);
      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: currentUser?.username || "",
            email: currentUser?.email || "",
            address: {
              line1: addressLine1,
              country,
              state,
              city,
              postal_code: postalCode,
            },
          },
        },
      });

      if (error) {
        const errorMessage = getPaymentErrorMessage(error, "Card setup failed.");
        setMessage(errorMessage);
        toast.error(errorMessage);
        return;
      }

      if (!setupIntent?.payment_method) {
        setMessage("Card setup completed, but no payment method was returned.");
        return;
      }

      await persistPaymentMethod(setupIntent.payment_method);
      toast.success("Payment method added.");
      closeCardModal();
    } catch (err) {
      console.log(err);
      const errorMessage = getPaymentErrorMessage(
        err,
        "Unable to save the payment method."
      );
      setMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const modalTitle = "ADD PAYMENT METHOD";

  const renderPaymentContent = () => {
    if (loading) {
      return (
        <div className={styles.LoadingWrap}>
          <Spinner animation="border" size="sm" />
          Loading payment methods...
        </div>
      );
    }

    if (customerMissing) {
      return (
        <Alert variant="warning" className="mb-0">
          No payment methods profile exists yet. Complete a subscription purchase first,
          then return here to manage your payment methods.
        </Alert>
      );
    }

    return (
      <>
        <section className={styles.SectionCard}>
          <h5 className={styles.SectionHeading}>Default Payment Method</h5>

          {hasDefaultCard ? (
            <>
              <PaymentMethodCard
                method={defaultPaymentMethod}
                isDefault
              />
              <Alert variant="light" className={styles.InfoAlert}>
                This is your default payment method. It will be used for all recurring
                payments.
              </Alert>
            </>
          ) : (
            <div className={styles.EmptyState}>
              <p className="mb-3">No default payment method.</p>
              <Button
                className={`${btnStyles.Button} ${btnStyles.Shed} ${styles.PrimaryActionButton}`}
                onClick={startCardFlow}
                disabled={loadingIntent || saving}
              >
                <i className="fas fa-plus" aria-hidden="true" />
                Add Payment Method
              </Button>
            </div>
          )}
        </section>

        <section className={styles.SectionCard}>
          <h5 className={styles.SectionHeading}>Other Payment Methods</h5>
          {otherCards.length > 0 ? (
            <div className={styles.MethodList}>
              {otherCards.map((method) => (
                <PaymentMethodCard
                  key={method?.id || `${method.brand}-${method.last4}`}
                  method={method}
                  onSetDefault={() => handleSetDefault(method)}
                  settingDefault={settingDefaultId === method?.id}
                />
              ))}
            </div>
          ) : (
            <p className={styles.OtherEmpty}>No other payment methods.</p>
          )}
        </section>
      </>
    );
  };

  return (
    <div className={styles.PageRoot}>
      <div className="mx-md-5">
        <TopBox title="Payment Methods" />
      </div>

      {!!subscriptionWarnings.length && (
        <Row className="mt-2 px-2">
          <Col md={{ span: 10, offset: 1 }}>
            <Alert variant="warning">
              <strong>Subscription sync warnings</strong>
              <ul className="mb-0 mt-2">
                {subscriptionWarnings.map((warning, index) => (
                  <li key={`${warning}-${index}`}>{warning}</li>
                ))}
              </ul>
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="mb-3 mt-4 px-2">
        <Col md={{ span: 10, offset: 1 }}>
          <div className="d-flex justify-content-between align-items-start flex-wrap mb-3">
            <div className="mb-2 mb-md-0">
              <h5 className="mb-1">Payment Methods</h5>
              <p className={`${styles.Subtitle} mb-0`}>
                Manage the payment methods for your subscription and future payments.
              </p>
            </div>
            {!customerMissing && !loading && (
              <Button
                className={`${btnStyles.Button} ${btnStyles.Shed} ${styles.PrimaryActionButton}`}
                onClick={startCardFlow}
                disabled={loadingIntent || saving}
              >
                <i className="fas fa-plus" aria-hidden="true" />
                Add Payment Method
              </Button>
            )}
          </div>

          {loading || customerMissing ? (
            <Card className={appStyles.Content}>
              <Card.Body>{renderPaymentContent()}</Card.Body>
            </Card>
          ) : (
            renderPaymentContent()
          )}
        </Col>
      </Row>

      <AddPaymentMethodModal
        show={showCardModal}
        title={modalTitle}
        saving={saving}
        message={message}
        cardElementOptions={cardElementOptions}
        onHide={closeCardModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
